/**
 * Customizations:
 * 1. Add function to notify add to cart action
 * 2. Temporary suppression of specific error message related to string pattern error.
 * 3. Handle specific error code for authentication cookie scenario, which requires special handling to clear cookies and reload the page.
 */

import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalizedErrorMessage } from '@/data/Localization';
import { ERR_AUTHENTICATION_COOKIE_NOT_IN_HTTPS_CODE, ERROR_TYPE } from '@/data/constants/errors';

import {
	NotificationMessageOptionsType,
	NotificationsContext,
	NotificationText,
} from '@/data/context/notifications';
import { SessionErrorContext } from '@/data/context/sessionError';
import { clearCurrentUserFromStorage } from '@/data/forReactAndMobile/SaveCurrentUser';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ErrorType, SessionErrorType } from '@/data/types/Error';
import {
	handleForUserSessionError,
	handleLockOrderError,
	isOrderLockError,
} from '@/data/utils/customerService';
import { generateShippingInfoError } from '@/data/utils/generateShippingInfoError';
import { error as logError } from '@/data/utils/loggerUtil';
import { isErrorType } from '@/data/utils/processError';
import { useCallback, useContext } from 'react';

// Temporary suppression of specific error message.
// As instructed by the HDMX team, this message is hidden to avoid confusing users.
// This is not considered a critical issue, so the toast is suppressed for now.
// TODO:  Root cause analysis should be performed later to properly address the underlying problem.
const suppressedMessages = ['The string did not match the expected pattern.'];

export const useNotifications = () => {
	const { message, setMessage } = useContext(NotificationsContext);
	const { setSessionError } = useContext(SessionErrorContext);
	const getLocalizedErrorMessage = useLocalizedErrorMessage();
	const router = useNextRouter();

	const showMessage = (message: NotificationText, options?: NotificationMessageOptionsType) => {
		setMessage({ text: message, ...options });
	};

	const showErrorMessage = (message: NotificationText) => {
		setMessage({ text: message, severity: 'error' });
	};

	const showSuccessMessage = (message: NotificationText, isAddToCart = false) => {
		setMessage({ text: message, severity: 'success', isAddToCart });
	};

	const notifyAddToCart = () => {
		setMessage({ text: '', isAddToCart: true });
	};

	const clearMessage = () => {
		setMessage({ text: '' });
	};
	const handleAuthenticationCookieError = useCallback(() => {
		// Halt pending requests and purge local storage.
		window.stop?.();
		clearCurrentUserFromStorage(process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '');

		// 1. Purge cookies via SSR API.
		// 2. Reload the page to use generic guest identity.
		fetch('/api/reset-session')
			.then(() => {
				router.reload();
			})
			.catch((err) => {
				logError(undefined, 'Notifications: notifyError: session refresh failed: %o', err);
			});
		return;
	}, [router]);

	const notifyError = useCallback(
		(error: TransactionErrorResponse | ErrorType) => {
			if (isErrorType(error)) {
				if (suppressedMessages.some((msg) => (error.errorMessage ?? '').includes(msg))) {
					return;
				} else if (error.error.errorCode === ERR_AUTHENTICATION_COOKIE_NOT_IN_HTTPS_CODE) {
					return handleAuthenticationCookieError();
				} else if (
					error.type === ERROR_TYPE.session &&
					!handleForUserSessionError(error.messageKey)
				) {
					setSessionError(error as SessionErrorType);
				} else if (error.type === ERROR_TYPE.shippingInfo) {
					setMessage({
						severity: 'error',
						...generateShippingInfoError({ text: getLocalizedErrorMessage(error) }),
					});
				} else if (!(isOrderLockError(error.error) && handleLockOrderError(error.error))) {
					setMessage({ text: getLocalizedErrorMessage(error), severity: 'error' });
				}
			} else {
				logError(undefined, 'Notifications: notifyError: error: %o', error);
			}
		},
		[getLocalizedErrorMessage, setMessage, setSessionError, handleAuthenticationCookieError]
	);

	return {
		message,
		showMessage,
		showSuccessMessage,
		showErrorMessage,
		clearMessage,
		notifyError,
		notifyAddToCart,
	};
};
